import './ViewerControls.css'
import React, { useState } from 'react'
import ComplexMessage from './ComplexMessage'
import classnames from 'classnames'
import DownloadButton from './DownloadButton'
import formatMessage from 'format-message'
import MenuDivider from './MenuDivider'
import AnnotationControls from './AnnotationControls'
import DocumentControls from './DocumentControls'
import SecondaryControls from './SecondaryControls'
import MediaQuery from 'react-responsive'
import { useDebounce } from 'react-use'

const validatePageNumber = (pageNum, numPages) => pageNum > 0 && pageNum <= numPages

export default function ViewerControls({
  showDownload,
  controlMode,
  currentBgColor,
  currentColor,
  currentFont,
  currentInkWidth,
  currentPageNum,
  currentPageScale,
  documentId,
  generateMergedPdf,
  isAnnotatable,
  isFullscreen,
  mergedPdfStatus,
  numPages,
  onControlModeChange,
  onCurrentBgColorChange,
  onCurrentColorChange,
  onCurrentInkStrokeWidthChange,
  onCurrentTextSizeChange,
  onPageChange,
  onPageScale,
  pageNumberToAnnotationNumber,
  rotatePageClockwise,
  selectedAnnotationId,
  toggleFullscreen,
  userRole,
}) {
  const [pageNumInputValue, setPageNumInputValue] = useState(currentPageNum)

  // TODO: remove need for useDebounce
  // PROBLEM: when traversing (e.g. from page 9 to 8),
  //   currentPageNum is sometimes temporarily set to 1
  useDebounce(
    () => {
      if (pageNumInputValue !== currentPageNum) {
        setPageNumInputValue(parseInt(currentPageNum, 10))
      }
    },
    50, // sadness
    [currentPageNum],
  )

  function onKeyPress(event) {
    if (event.key === 'Enter') {
      onPageChange_(pageNumInputValue)
    }
  }

  function resetInputIfInvalid() {
    if (!validatePageNumber(pageNumInputValue, numPages)) {
      setPageNumInputValue(currentPageNum)
    }
  }

  function onInputChange(event) {
    if (event.target.value == '') return
    const newPageNum =
      event.target && typeof event.target.value === 'string'
        ? parseInt(event.target.value, 10)
        : pageNumInputValue
    setPageNumInputValue(newPageNum)
  }

  function onPageChange_(newPageNum) {
    if (validatePageNumber(newPageNum, numPages)) {
      setPageNumInputValue(newPageNum)
      onPageChange(newPageNum - 1)
    } else {
      setPageNumInputValue(currentPageNum)
    }
  }

  const classNames = classnames('ViewerControls', {
    'ViewerControls--with-annotations': isAnnotatable,
  })
  return (
    <nav className={classNames}>
      <div className="ViewerControls-primary">
        {showDownload ? (
          <div className="ViewerControls--download">
            <DownloadButton
              generateMergedPdf={generateMergedPdf}
              mergedPdfStatus={mergedPdfStatus}
            />
            <MenuDivider style={{ marginRight: '18px' }} />
          </div>
        ) : (
          ''
        )}
        <div className="ViewerControls--title" />
        {numPages > 1 && (
          <div className="ViewerControls--page-controls">
            <ComplexMessage
              className="ViewerControls--page-number"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {formatMessage('Page {pageInput} of {totalPages, number}', {
                pageInput: ComplexMessage.placeholder('pageInput'),
                totalPages: numPages,
              })}
              <div key="pageInput">
                <button
                  disabled={currentPageNum === 1}
                  className="AnnotationControlButton"
                  aria-label={formatMessage('Previous Page')}
                  title={formatMessage('Previous Page')}
                  onClick={() => onPageChange_(currentPageNum - 1)}
                >
                  <div className="AnnotationControlButton-outer">
                    <div className="AnnotationControlButton-inner">
                      <svg viewBox="0 0 2000 2000" height="16" width="16">
                        <path
                          d="M1352.005.012l176.13 176.13L744.27 960.131l783.864 783.74L1352.005 1920 391.887 960.13z"
                          fill="#F5F5F5"
                          fillRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </button>
                <input
                  aria-label={formatMessage('Current Page Number')}
                  className="ViewerControls--current-page"
                  value={pageNumInputValue}
                  onBlur={resetInputIfInvalid}
                  onChange={onInputChange}
                  onKeyPress={onKeyPress}
                  type="number"
                  min="1"
                  max={numPages}
                  step="1"
                  disabled={numPages === 1}
                />
                <button
                  disabled={currentPageNum === numPages}
                  className="AnnotationControlButton"
                  aria-label={formatMessage('Next Page')}
                  title={formatMessage('Next Page')}
                  onClick={() => onPageChange_(currentPageNum + 1)}
                >
                  <div className="AnnotationControlButton-outer">
                    <div className="AnnotationControlButton-inner">
                      <svg viewBox="0 0 2000 2000" height="16" width="16">
                        <path
                          d="M568.13.012L392 176.142l783.864 783.989L392 1743.87 568.13 1920l960.118-959.87z"
                          fill="#F5F5F5"
                          fillRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </button>
              </div>
            </ComplexMessage>
            <MenuDivider style={{ marginLeft: '16px', marginRight: '10px' }} />
          </div>
        )}
        <DocumentControls
          currentPageNum={currentPageNum}
          currentPageScale={currentPageScale}
          documentId={documentId}
          isFullscreen={isFullscreen}
          onPageScale={onPageScale}
          pageNumberToAnnotationNumber={pageNumberToAnnotationNumber}
          rotatePageClockwise={() => rotatePageClockwise(currentPageNum - 1)}
          toggleFullscreen={toggleFullscreen}
          isAnnotatable={isAnnotatable}
          userRole={userRole}
        />
        <MediaQuery minDeviceWidth={938}>
          <AnnotationControls
            isAnnotatable={isAnnotatable}
            controlMode={controlMode}
            onChange={onControlModeChange}
          />
        </MediaQuery>
      </div>

      <MediaQuery maxDeviceWidth={937}>
        {isAnnotatable && (
          <div>
            <hr style={{ margin: 0 }} aria-hidden={true} />
            <div className="ViewerControls-primary-overflow" style={{ float: 'right' }}>
              <AnnotationControls
                isAnnotatable={isAnnotatable}
                controlMode={controlMode}
                onChange={onControlModeChange}
              />
            </div>
          </div>
        )}
      </MediaQuery>

      {!selectedAnnotationId && (
        <SecondaryControls
          controlMode={controlMode}
          currentColor={currentColor}
          currentBgColor={currentBgColor}
          currentInkWidth={currentInkWidth}
          currentFont={currentFont}
          onCurrentColorChange={onCurrentColorChange}
          onCurrentBgColorChange={onCurrentBgColorChange}
          onCurrentInkStrokeWidthChange={onCurrentInkStrokeWidthChange}
          onCurrentTextSizeChange={onCurrentTextSizeChange}
        />
      )}
    </nav>
  )
}
